import React, { useState } from 'react';

import classNames from 'classnames';
import AlertIcon from 'icons/alert-icon.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import voca from 'voca';

import FilledButton from 'components/application/buttons/FilledButton';
import OutlinedButton from 'components/application/buttons/OutlinedButton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import AllowanceExplanationModal from 'components/settings/users/AllowanceExplanationModal';

import * as IntercomHelperModule from 'components/helpers/intercom';

export default function AllowanceBar({
  areProjectsEnabled,
  fullUserAllowanceConsumptionCount,
  fullUserAllowanceCount,
  isAtFullUserCapacity,
  isAtPersonnelReporteeManagerCapacity,
  isPersonnelReporteeManagerEnabled,
  personnelReporteeManagerAllowanceConsumptionCount,
  personnelReporteeManagerAllowanceCount,
}) {
  const currentActor = useCurrentActor();
  const { pluralRAMSName } = currentActor.division.attributes;

  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);

  const { t: i18nTCommon } = useTranslation('common');
  const { t: i18nTComponents } = useTranslation('components');

  const isFullUserAllowanceUnlimited = fullUserAllowanceCount == null;
  const isPersonnelReporteeManagerAllowanceUnlimited =
    (personnelReporteeManagerAllowanceCount ?? null) === null;

  const isAtPersonnelReporteeManagerLimit =
    isAtPersonnelReporteeManagerCapacity &&
    personnelReporteeManagerAllowanceCount !== 0;

  const isAtAnyCapacity =
    isAtFullUserCapacity ||
    (isPersonnelReporteeManagerEnabled && isAtPersonnelReporteeManagerLimit);

  const areAllShownAllowancesUnlimited =
    isFullUserAllowanceUnlimited &&
    (!isPersonnelReporteeManagerEnabled ||
      isPersonnelReporteeManagerAllowanceUnlimited);

  const label = (key) =>
    i18nTComponents(`settings.users.AllowanceBar.labels.${key}`);

  const UpgradeButton = isAtAnyCapacity ? FilledButton : OutlinedButton;

  const handleUpgradeClick = () => {
    IntercomHelperModule.launchIntercomWithMessage(
      "Hello, I'm interested in upgrading my plan.",
    );
  };

  return (
    <>
      <div className='tw-m-0 tw-mb-5 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-px-5 tw-py-4'>
        <div className='tw-min-w-0'>
          <div className='tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-8 tw-gap-y-2'>
            <AllowanceCount
              consumingCount={fullUserAllowanceConsumptionCount}
              hasAlertIcon={true}
              isAtCapacity={isAtFullUserCapacity}
              isUnlimited={isFullUserAllowanceUnlimited}
              label={label(
                isAtFullUserCapacity ? 'fullUsersLimitReached' : 'fullUsers',
              )}
              totalCount={fullUserAllowanceCount}
            />
            {isPersonnelReporteeManagerEnabled && (
              <AllowanceCount
                consumingCount={
                  personnelReporteeManagerAllowanceConsumptionCount
                }
                isAtCapacity={isAtPersonnelReporteeManagerLimit}
                isUnlimited={isPersonnelReporteeManagerAllowanceUnlimited}
                label={label(
                  isAtPersonnelReporteeManagerLimit ?
                    'personnelReporteeManagersLimitReached'
                  : 'personnelReporteeManagers',
                )}
                totalCount={personnelReporteeManagerAllowanceCount}
              />
            )}
          </div>
          <p className='tw-m-0 tw-mt-3 tw-text-m tw-text-grey-900'>
            {areProjectsEnabled && (
              <>
                <Trans
                  i18nKey={
                    isAtAnyCapacity ?
                      'settings.users.AllowanceBar.limitReachedProjectViewerDescription'
                    : 'settings.users.AllowanceBar.limitNotReachedProjectViewerDescription'
                  }
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    pluralRAMSName:
                      isAtAnyCapacity ? pluralRAMSName : (
                        voca.capitalize(pluralRAMSName)
                      ),
                  }}
                />{' '}
              </>
            )}
            <button
              className='tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-grey-900 tw-underline'
              onClick={() => setIsExplanationModalOpen(true)}
              type='button'
            >
              {i18nTCommon('shared.learnMore')}
            </button>
          </p>
        </div>
        {!areAllShownAllowancesUnlimited && (
          <UpgradeButton
            className='tw-shrink-0'
            onClick={handleUpgradeClick}
            {...(isAtAnyCapacity ?
              { color: 'blue' }
            : { color: 'grey', size: 'sm' })}
          >
            {i18nTCommon('shared.upgradePlan')}
          </UpgradeButton>
        )}
      </div>
      {isExplanationModalOpen && (
        <AllowanceExplanationModal
          onClose={() => setIsExplanationModalOpen(false)}
        />
      )}
    </>
  );
}

AllowanceBar.propTypes = {
  areProjectsEnabled: PropTypes.bool.isRequired,
  isPersonnelReporteeManagerEnabled: PropTypes.bool.isRequired,
  fullUserAllowanceConsumptionCount: PropTypes.number.isRequired,
  personnelReporteeManagerAllowanceConsumptionCount:
    PropTypes.number.isRequired,
  personnelReporteeManagerAllowanceCount: PropTypes.number,
  fullUserAllowanceCount: PropTypes.number,
  isAtFullUserCapacity: PropTypes.bool.isRequired,
  isAtPersonnelReporteeManagerCapacity: PropTypes.bool.isRequired,
};

function AllowanceCount({
  consumingCount,
  hasAlertIcon,
  isAtCapacity,
  isUnlimited,
  label,
  totalCount,
}) {
  const mainTextColor = isAtCapacity ? 'tw-text-red-600' : 'tw-text-grey-900';

  return (
    <span className='tw-flex tw-items-baseline tw-gap-x-3'>
      <span>
        <Trans
          components={{
            countSpan: (
              <span
                className={classNames(
                  'tw-text-2xl tw-font-bold tw-tracking-tighter',
                  mainTextColor,
                )}
              />
            ),
            totalSpan: (
              <span
                className={classNames(
                  'tw-text-l tw-font-semibold',
                  isAtCapacity ? 'tw-text-red-700' : 'tw-text-grey-900',
                )}
              />
            ),
          }}
          i18nKey={
            isUnlimited ?
              'settings.users.AllowanceBar.counts.unlimited'
            : 'settings.users.AllowanceBar.counts.limited'
          }
          ns='components'
          values={{ consumingCount, totalCount }}
        />
      </span>
      <span
        className={classNames(
          'tw-whitespace-nowrap tw-text-m tw-font-medium',
          mainTextColor,
        )}
      >
        {isAtCapacity && hasAlertIcon && (
          <AlertIcon
            className='tw-mr-1 tw-inline-block tw-text-red-600'
            height={14}
            width={16}
          />
        )}
        {label}
      </span>
    </span>
  );
}

AllowanceCount.propTypes = {
  consumingCount: PropTypes.number.isRequired,
  hasAlertIcon: PropTypes.bool,
  isAtCapacity: PropTypes.bool.isRequired,
  isUnlimited: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  totalCount: PropTypes.number,
};
